import { useEffect, useState } from 'react';
import { formatDate } from './utils/formatDate';

// Trade History = the BOOKS' record of closed trades (Oct 2026). Replaces the old "your recorded
// entries & exits" (tracked positions are retired — everything is the mirrored book). Shows trades
// closed since the subscriber joined (never before the Sep 30 2026 reset). Preserver for everyone;
// Maximizer subscribers also see the breakout book.

const REASON = {
  trailing_stop: '30% trailing stop',
  market_regime: 'S&P below 200-day',
  hold_exit: 'Day-29 exit',
  cash_merger: 'Acquired — cash',
  stock_merger: 'Acquired — stock',
  stock_and_cash_merger: 'Acquired',
  spin_off: 'Spin-off',
  renamed: 'Ticker change',
  redemption: 'Redeemed',
  worthless_removal: 'Removed',
};

const Metric = ({ label, value, sub }) => (
  <div className="px-3 py-2">
    <div className="font-body text-[0.6rem] font-medium tracking-[0.18em] uppercase text-ink-mute">{label}</div>
    <div className="font-display text-[1.6rem] text-ink" style={{ fontVariationSettings: '"opsz" 48' }}>{value}</div>
    {sub && <div className="font-mono text-[0.7rem] text-ink-mute">{sub}</div>}
  </div>
);

function BookSection({ title, note, book, emptyText, onRowClick }) {
  const closed = book?.closed || [];
  const s = book?.summary || {};
  return (
    <div className="overflow-hidden">
      <div className="pb-3 border-b-2 border-ink mb-4">
        <h2 className="font-display text-[1.25rem] font-medium text-ink tracking-tight" style={{ fontVariationSettings: '"opsz" 48' }}>
          {title} <em className="font-display italic text-ink-mute text-[0.85rem]" style={{ fontVariationSettings: '"opsz" 24' }}>{note}</em>
        </h2>
      </div>
      <div className="grid grid-cols-3 border-b border-rule mb-4">
        <Metric label="Trades" value={s.trades ?? 0} />
        <Metric label="Win rate" value={s.win_rate != null ? `${Math.round(s.win_rate)}%` : '—'}
                sub={s.trades ? `${s.wins}W / ${s.trades - s.wins}L` : null} />
        <Metric label="Avg return" value={s.avg_return_pct != null ? `${s.avg_return_pct >= 0 ? '+' : ''}${s.avg_return_pct.toFixed(1)}%` : '—'} />
      </div>
      {closed.length > 0 ? (
        <>
        {/* Phones: one card per trade, return large on the right (the table clipped the Return
            column — mobile audit Oct 29). Table stays for sm+. */}
        <div className="sm:hidden border-t border-rule">
          {closed.map((t, i) => (
            <button key={`m-${t.symbol}-${t.exit_date}-${i}`} type="button" onClick={() => onRowClick && onRowClick(t)}
                    className="w-full text-left px-1 py-3.5 border-b border-rule flex items-center justify-between gap-3 active:bg-paper-card">
              <span className="flex flex-col gap-1 min-w-0">
                <span className="font-display text-[1.15rem] font-medium text-ink" style={{ fontVariationSettings: '"opsz" 48' }}>{t.symbol}</span>
                <span className="font-mono text-[0.75rem] text-ink-mute">
                  {t.entry_date ? formatDate(t.entry_date) : '—'} → {formatDate(t.exit_date)}{t.days_held != null ? ` · ${t.days_held}d` : ''}
                </span>
                <span className="font-mono text-[0.75rem] text-ink-mute">
                  {t.entry_price != null ? `$${t.entry_price.toFixed(2)}` : '—'} → ${t.exit_price?.toFixed(2)} · {REASON[t.exit_reason] || (t.exit_reason || '').replace(/_/g, ' ')}
                </span>
              </span>
              {t.pnl_pct != null && (
                <span className={`font-display text-[1.5rem] whitespace-nowrap ${t.pnl_pct >= 0 ? 'text-positive' : 'text-negative'}`} style={{ fontVariationSettings: '"opsz" 48' }}>
                  {t.pnl_pct >= 0 ? '+' : '−'}{Math.abs(t.pnl_pct).toFixed(1)}%
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full border-collapse" style={{ fontFeatureSettings: '"tnum"' }}>
            <thead>
              <tr>{['Symbol', 'Entry', 'Exit', 'Entry $', 'Exit $', 'Return', 'Why it closed', 'Days'].map(h =>
                <th key={h} className="py-2 px-3 text-left font-body text-[0.62rem] font-medium tracking-[0.2em] uppercase text-ink-mute border-b border-ink">{h}</th>)}</tr>
            </thead>
            <tbody>
              {closed.map((t, i) => (
                <tr key={`${t.symbol}-${t.exit_date}-${i}`} onClick={() => onRowClick && onRowClick(t)}
                    className="hover:bg-paper-card border-b border-rule cursor-pointer">
                  <td className="py-3 px-3 font-display text-[1.05rem] font-medium" style={{ fontVariationSettings: '"opsz" 48' }}>{t.symbol}</td>
                  <td className="py-3 px-3 font-mono text-[0.85rem] text-ink-mute">{t.entry_date ? formatDate(t.entry_date) : '—'}</td>
                  <td className="py-3 px-3 font-mono text-[0.85rem] text-ink-mute">{formatDate(t.exit_date)}</td>
                  <td className="py-3 px-3 font-mono text-[0.88rem]">{t.entry_price != null ? `$${t.entry_price.toFixed(2)}` : '—'}</td>
                  <td className="py-3 px-3 font-mono text-[0.88rem]">${t.exit_price?.toFixed(2)}</td>
                  <td className="py-3 px-3 font-mono text-[0.88rem]">
                    {t.pnl_pct != null && <span className={`font-medium ${t.pnl_pct >= 0 ? 'text-positive' : 'text-negative'}`}>{t.pnl_pct >= 0 ? '+' : ''}{t.pnl_pct.toFixed(1)}%</span>}
                  </td>
                  <td className="py-3 px-3"><span className="font-mono text-[0.68rem] tracking-[0.12em] uppercase text-ink-mute px-2 py-1 border border-rule-dark">{REASON[t.exit_reason] || (t.exit_reason || '').replace(/_/g, ' ')}</span></td>
                  <td className="py-3 px-3 font-mono text-[0.85rem] text-ink-mute">{t.days_held != null ? `${t.days_held}d` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      ) : (
        <div className="text-center py-10 text-ink-mute">
          <p className="font-display italic" style={{ fontVariationSettings: '"opsz" 24' }}>{emptyText}</p>
        </div>
      )}
    </div>
  );
}

export default function BookTradeHistory({ api, onRowClick }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let live = true;
    // Forward an admin's ?preview_tier= so History matches the tier being previewed.
    const _pt = new URLSearchParams(window.location.search).get('preview_tier');
    api.get(`/api/portfolio/book-trades${_pt ? `?preview_tier=${encodeURIComponent(_pt)}` : ''}`)
      .then(r => { if (live) setData(r); })
      .catch(() => { if (live) setErr(true); });
    return () => { live = false; };
  }, [api]);

  if (err) return <p className="text-ink-mute py-10 text-center">Trade history is unavailable right now — try again shortly.</p>;
  if (!data) return <p className="text-ink-mute py-10 text-center font-display italic">Loading the book's record…</p>;

  const since = formatDate(data.since);
  const note = data.since_joined ? `Closed since you joined · ${since}` : `Closed since ${since}`;
  return (
    <div className="space-y-10">
      <BookSection
        title="Preserver book"
        note={note}
        book={data.preserver}
        onRowClick={onRowClick}
        emptyText="Nothing has closed yet — the book lets winners run on a 30% trailing stop."
      />
      {data.maximizer && (
        <BookSection
          title="Maximizer book"
          note={note}
          book={data.maximizer}
          onRowClick={onRowClick}
          emptyText={`No breakouts have closed yet — ${data.maximizer.open || 0} are open, each sells on its day-29 time-stop.`}
        />
      )}
      <p className="font-mono text-[0.7rem] text-ink-light">
        The model books' actual closes — the trades you'd have made mirroring the book. Signals only; execute via your broker.
      </p>
    </div>
  );
}
